import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import SowSelect from '../shared/SowSelect';

const STATUS_LABELS = {
  in_stock:       'In Stock',
  deployed:       'Deployed',
  allocated:      'Allocated',
  in_transit:     'In Transit',
  decommissioned: 'Decommissioned',
};

const STATUS_COLORS = {
  in_stock:       { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: '#10B981' },
  deployed:       { bg: 'bg-blue-100',    text: 'text-blue-800',    dot: '#3B82F6' },
  allocated:      { bg: 'bg-amber-100',   text: 'text-amber-800',   dot: '#F59E0B' },
  in_transit:     { bg: 'bg-purple-100',  text: 'text-purple-800',  dot: '#8B5CF6' },
  decommissioned: { bg: 'bg-gray-100',    text: 'text-gray-500',    dot: '#9CA3AF' },
};

const QUICK_STATUSES = ['in_stock', 'deployed', 'allocated', 'in_transit'];

export default function ScanPage({ assetTag }) {
  const { toast } = useToast();
  const [item, setItem]       = useState(null);
  const [sows, setSows]       = useState([]);
  const [locations, setLocations] = useState([]);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction]   = useState(null);   // null | status string | '__move'
  const [sowId, setSowId]     = useState('');
  const [moveLoc, setMoveLoc] = useState('');
  const [notes, setNotes]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [done, setDone]       = useState(null);   // success message
  const [stState, setStState] = useState(null);   // stock-take confirm: 'done' | 'err:<msg>'

  useEffect(() => {
    Promise.all([
      api.get(`inventory/by-tag/${assetTag}`),
      api.get('sows', { status: 'Active' }).catch(() => []),
      api.get('inventory/locations').catch(() => []),
    ]).then(([it, s, l]) => {
      setItem(it);
      setSows(s);
      setLocations(l);
      setLoading(false);
    }).catch(e => {
      setError(e.message || 'Item not found');
      setLoading(false);
    });
  }, [assetTag]);

  const startAction = (status) => {
    setAction(status);
    setSowId('');
    setNotes('');
  };

  const startMove = () => {
    setAction('__move');
    setSowId('');
    setNotes('');
    // Pre-select the last location used on this device (common when bulk-moving a batch)
    const last = localStorage.getItem('av_last_inv_location') || '';
    setMoveLoc(last && last !== item.location_id ? last : '');
  };

  const submit = async () => {
    // Location move — no status change, just relocate the item
    if (action === '__move') {
      if (!moveLoc) { toast.error('Pick a location, or choose “Unknown / no location”.'); return; }
      const newLocId = moveLoc === '__none__' ? null : moveLoc;
      setSaving(true);
      try {
        await api.put(`inventory/${item.id}`, {
          location_id: newLocId,
          movement_notes: notes || undefined,
        });
        if (newLocId) localStorage.setItem('av_last_inv_location', newLocId);
        const updated = await api.get(`inventory/by-tag/${assetTag}`);
        setItem(updated);
        const locName = newLocId ? (locations.find(l => l.id === newLocId)?.name || 'new location') : 'Unknown (no location)';
        setDone(`Moved to "${locName}" successfully.`);
        setAction(null);
      } catch (e) {
        toast.error('Failed: ' + e.message);
      }
      setSaving(false);
      return;
    }
    if (action === 'allocated' && !sowId && !notes.trim()) {
      toast.error('Please link a SOW or provide a reason for allocation.');
      return;
    }
    setSaving(true);
    try {
      await api.put(`inventory/${item.id}`, {
        status: action,
        allocated_to_sow_id: action === 'allocated' ? (sowId || null) : undefined,
        notes: notes || undefined,
        movement_notes: notes || undefined,
      });
      // Reload item to show new status
      const updated = await api.get(`inventory/by-tag/${assetTag}`);
      setItem(updated);
      setDone(`Status updated to "${STATUS_LABELS[action]}" successfully.`);
      setAction(null);
    } catch (e) {
      toast.error('Failed: ' + e.message);
    }
    setSaving(false);
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.fullscreen}>
        <div style={styles.card}>
          <AVWordmark />
          <p style={{ color: '#9CA3AF', fontSize: 12, marginTop: 16 }}>Loading item…</p>
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div style={styles.fullscreen}>
        <div style={styles.card}>
          <AVWordmark />
          <div style={{ marginTop: 20, padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8 }}>
            <p style={{ color: '#DC2626', fontSize: 13, fontWeight: 600, margin: 0 }}>Item not found</p>
            <p style={{ color: '#EF4444', fontSize: 11, marginTop: 4, margin: '4px 0 0' }}>{assetTag}</p>
          </div>
          <a href="/" style={styles.backLink}>← Return to app</a>
        </div>
      </div>
    );
  }

  const sc = STATUS_COLORS[item.status] || STATUS_COLORS.in_stock;

  // Stock-take mode: a session is active on this device (set by the ?stocktake= page).
  const activeStockTake = (() => { try { return localStorage.getItem('activeStockTake'); } catch (e) { return null; } })();
  const confirmStockTake = async () => {
    try {
      await api.post(`stock-takes/${activeStockTake}/confirm`, { item_id: item.id, method: 'scan' });
      setStState('done');
    } catch (e) {
      setStState('err:' + (e?.data?.error || e.message || 'Could not confirm'));
    }
  };

  // ── Move-location panel ────────────────────────────────────────────────────────
  if (action === '__move') {
    return (
      <div style={styles.fullscreen}>
        <div style={styles.card}>
          <AVWordmark />

          {/* Item summary strip */}
          <div style={{ marginTop: 16, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1B2A4A' }}>{item.name}</div>
            <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
              {item.asset_tag}{item.location_name ? ` · currently @ ${item.location_name}` : ' · no location set'}
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={styles.label}>Move to location *</label>
            <select style={styles.input} value={moveLoc} onChange={e => setMoveLoc(e.target.value)} autoFocus>
              <option value="">— Select location —</option>
              <option value="__none__">— Unknown / no location —</option>
              {locations.filter(l => l.id !== item.location_id).map(l => (
                <option key={l.id} value={l.id}>{l.name}{l.item_count != null ? ` (${l.item_count} items)` : ''}</option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={styles.label}>Notes (optional)</label>
            <textarea
              style={{ ...styles.input, height: 60, resize: 'none' }}
              placeholder="e.g. Loaded into van for site delivery…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={() => setAction(null)} style={styles.btnSecondary}>Cancel</button>
            <button onClick={submit} disabled={saving || !moveLoc} style={{ ...styles.btnPrimary, flex: 1, opacity: (saving || !moveLoc) ? 0.5 : 1 }}>
              {saving ? 'Saving…' : 'Confirm Move'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Action panel ─────────────────────────────────────────────────────────────
  if (action) {
    const asc = STATUS_COLORS[action] || STATUS_COLORS.in_stock;
    return (
      <div style={styles.fullscreen}>
        <div style={styles.card}>
          <AVWordmark />

          {/* Item summary strip */}
          <div style={{ marginTop: 16, padding: '10px 12px', background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1B2A4A' }}>{item.name}</div>
            <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>{item.asset_tag}{item.serial_number ? ` · S/N ${item.serial_number}` : ''}</div>
          </div>

          {/* New status badge */}
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: '#6B7280' }}>Setting to:</span>
            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: asc.dot + '22', color: asc.dot }}>
              {STATUS_LABELS[action]}
            </span>
          </div>

          {/* SOW picker — only when allocating */}
          {action === 'allocated' && (
            <div style={{ marginTop: 12 }}>
              <label style={styles.label}>Link to SOW <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span></label>
              <SowSelect sows={sows} value={sowId} onChange={setSowId} placeholder="— Not linked to a SOW —" inputClassName="form-input text-[11px] w-full" />
            </div>
          )}

          {/* Notes */}
          <div style={{ marginTop: 12 }}>
            <label style={styles.label}>
              {action === 'allocated' && !sowId ? 'Reason for allocation *' : 'Notes (optional)'}
            </label>
            <textarea
              style={{ ...styles.input, height: 72, resize: 'none' }}
              placeholder={action === 'allocated' && !sowId ? 'e.g. Reserved for upcoming work…' : 'e.g. Deployed to site, rack 3A…'}
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={() => setAction(null)} style={styles.btnSecondary}>Cancel</button>
            <button onClick={submit} disabled={saving} style={{ ...styles.btnPrimary, flex: 1 }}>
              {saving ? 'Saving…' : `Confirm — ${STATUS_LABELS[action]}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main scan view ────────────────────────────────────────────────────────────
  return (
    <div style={styles.fullscreen}>
      <div style={styles.card}>
        <AVWordmark />

        {/* Success toast */}
        {done && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: '#ECFDF5', border: '1px solid #6EE7B7', borderRadius: 8 }}>
            <p style={{ color: '#065F46', fontSize: 12, margin: 0, fontWeight: 600 }}>✓ {done}</p>
          </div>
        )}

        {/* Stock-take confirm — shown when a stock take is active on this device */}
        {activeStockTake && (
          <div style={{ marginTop: 12, padding: '12px 14px', background: '#EFF6FF', border: '1px solid #93C5FD', borderRadius: 8 }}>
            {stState === 'done' ? (
              <p style={{ color: '#065F46', fontSize: 12, margin: 0, fontWeight: 700 }}>✓ Confirmed in stock take — scan the next item.</p>
            ) : stState && stState.startsWith('err:') ? (
              <p style={{ color: '#B91C1C', fontSize: 12, margin: 0, fontWeight: 600 }}>{stState.slice(4)}</p>
            ) : (
              <>
                <p style={{ color: '#1E3A8A', fontSize: 11, margin: '0 0 8px', fontWeight: 600 }}>Stock take in progress on this device.</p>
                <button onClick={confirmStockTake}
                  style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#10B981', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}>
                  ✓ Confirm this item is in stock
                </button>
              </>
            )}
          </div>
        )}

        {/* Item header */}
        <div style={{ marginTop: 16, borderBottom: '1px solid #F3F4F6', paddingBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#1B2A4A', lineHeight: 1.2 }}>{item.name}</div>
          {(item.catalog_description || item.description) && (
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>{item.catalog_description || item.description}</div>
          )}
          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#A1723D', letterSpacing: 1 }}>{item.asset_tag}</span>
            <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: sc.dot + '22', color: sc.dot }}>
              {STATUS_LABELS[item.status] || item.status}
            </span>
          </div>
          {item.merged_from && (
            <div style={{ fontSize: 10, color: '#6B7280', marginTop: 6 }}>
              Label {item.merged_from} was merged into {item.asset_tag}{item.quantity > 1 ? `, which holds ${item.quantity} units` : ''}.
            </div>
          )}
        </div>

        {/* Detail grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginTop: 14 }}>
          {item.serial_number && <DetailRow label="Serial No." value={item.serial_number} />}
          {item.category     && <DetailRow label="Category"   value={item.category} capitalize />}
          {item.location_name && <DetailRow label="Location"  value={item.location_name} />}
          {item.condition_state && <DetailRow label="Condition" value={item.condition_state} capitalize />}
          {item.status === 'allocated' && item.allocated_sow_reference && (
            <DetailRow label="Allocated to" value={`${item.allocated_sow_reference}${item.allocated_sow_detail ? ' — ' + item.allocated_sow_detail : ''}`} span />
          )}
          {item.status === 'allocated' && !item.allocated_sow_reference && item.notes && (
            <DetailRow label="Allocation reason" value={item.notes} span />
          )}
        </div>

        {/* Quick-action buttons */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
            Update Status
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {QUICK_STATUSES.filter(s => s !== item.status).map(s => {
              const c = STATUS_COLORS[s];
              return (
                <button
                  key={s}
                  onClick={() => startAction(s)}
                  style={{
                    padding: '10px 8px',
                    borderRadius: 8,
                    border: `1px solid ${c.dot}44`,
                    background: c.dot + '11',
                    color: c.dot,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontFamily: 'Montserrat, sans-serif',
                  }}
                >
                  {STATUS_LABELS[s]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Location move — the most common warehouse action */}
        {locations.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
              Location
            </div>
            <button
              onClick={startMove}
              style={{
                width: '100%',
                padding: '10px 8px',
                borderRadius: 8,
                border: '1px solid #0EA5E944',
                background: '#0EA5E911',
                color: '#0369A1',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                textAlign: 'center',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              🚚 Move Location{item.location_name ? ` · now @ ${item.location_name}` : ''}
            </button>
          </div>
        )}

        <a href="/" style={styles.backLink}>← Return to app</a>
      </div>
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function AVWordmark() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <img src="/av-logo-colour.svg" width={28} height={28} alt="Alton Valley"
        style={{ objectFit: 'contain', borderRadius: 4 }} />
      <span style={{ fontSize: 13, fontWeight: 800, color: '#1B2A4A', letterSpacing: 1 }}>ALTON VALLEY</span>
    </div>
  );
}

function DetailRow({ label, value, capitalize, span }) {
  return (
    <div style={{ gridColumn: span ? '1 / -1' : undefined }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#1B2A4A', marginTop: 2, fontWeight: 500, textTransform: capitalize ? 'capitalize' : undefined }}>{value}</div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  fullscreen: {
    minHeight: '100vh',
    background: '#F3F4F6',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '16px 0 32px',
    fontFamily: 'Montserrat, system-ui, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    padding: '20px 20px 24px',
    margin: '0 12px',
  },
  label: {
    display: 'block',
    fontSize: 10,
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: '9px 10px',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'Montserrat, sans-serif',
    color: '#1B2A4A',
    background: '#fff',
    boxSizing: 'border-box',
    outline: 'none',
  },
  btnPrimary: {
    padding: '12px 16px',
    background: '#A1723D',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
  },
  btnSecondary: {
    padding: '12px 16px',
    background: '#F3F4F6',
    color: '#374151',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
  },
  backLink: {
    display: 'block',
    marginTop: 20,
    textAlign: 'center',
    fontSize: 11,
    color: '#9CA3AF',
    textDecoration: 'none',
  },
};
